// Leert die Offline-Outbox: pro faelliger Zeile ein Einzelaufruf (kein Bulk, begrenzte
// Parallelitaet -- hier: strikt sequenziell), mit Backoff pro Zeile. Wird sowohl vom
// Hauptthread (online-Event, Sichtbarkeits-Wechsel, Intervall) als auch vom Service Worker
// (Background-Sync-Event, siehe sw.ts) aufgerufen -- dieselbe Funktion, IndexedDB ist aus
// beiden Kontexten erreichbar.

import { getPendingEntries, markConfirmed, recordAttempt, type OutboxEntry } from './db'
import { submitCheckin } from './api'

const BASE_DELAY_MS = 5_000
const MAX_DELAY_MS = 5 * 60_000

export function backoffDelayMs(attempts: number): number {
  if (attempts === 0) return 0
  const exponent = Math.min(attempts - 1, 6) // 5s * 2^6 = 320s, dann Deckel greift
  return Math.min(BASE_DELAY_MS * 2 ** exponent, MAX_DELAY_MS)
}

function isDue(entry: OutboxEntry, now: number): boolean {
  if (entry.attempts === 0 || !entry.lastAttemptAt) return true
  const lastAttempt = new Date(entry.lastAttemptAt).getTime()
  return now - lastAttempt >= backoffDelayMs(entry.attempts)
}

export interface FlushResult {
  confirmed: number
  remaining: number
}

export async function flushOutbox(): Promise<FlushResult> {
  const pending = await getPendingEntries()
  const now = Date.now()
  // Aelteste zuerst (Reihenfolge-Absicherung ueber monotonicCounter, siehe Architektur §2).
  const due = pending
    .filter((entry) => isDue(entry, now))
    .sort((a, b) => a.monotonicCounter - b.monotonicCounter)

  let confirmed = 0

  for (const entry of due) {
    try {
      const result = await submitCheckin({
        eventId: entry.eventId,
        deviceId: entry.deviceId,
        tenantSlug: entry.tenantSlug,
        employeeIdentifier: entry.employeeIdentifier,
        logType: entry.logType,
        clientTimeIso: entry.clientTimeIso,
        monotonicCounter: entry.monotonicCounter,
        deviceSignature: entry.signature,
        auftragId: entry.auftragId,
        taetigkeitGeplant: entry.taetigkeitGeplant,
        taetigkeitIst: entry.taetigkeitIst,
        zusatzgeraet: entry.zusatzgeraet,
      })
      // "accepted" oder already_known=true sind beide ein Erfolg -- siehe CheckinResponse-
      // Dokumentation in stempel-api: Idempotenz ist gewollt, kein Fehlerfall.
      if (result.status === 'accepted' || result.alreadyKnown) {
        await markConfirmed(entry.eventId)
        confirmed++
      } else {
        await recordAttempt(entry.eventId, `Unerwarteter Status: ${result.status}`)
      }
    } catch (error) {
      await recordAttempt(entry.eventId, error instanceof Error ? error.message : String(error))
    }
  }

  const remaining = (await getPendingEntries()).length
  return { confirmed, remaining }
}
